import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { PullSalesforceReportState } from "./pull-salesforce-report-state";
import { Checkbox, PrimaryButton } from "@fluentui/react";
import TitledPage from "@v2/components/layout/TitledPage";
import { FlowstepContainer } from "@v2/components/flowsteps/editors/common/FlowstepContainer/FlowstepContainer";
import { PropertyEdit } from "@v2/components/flowsteps/editors/hubspot/objects/PullOjbectHS/PropertyEdit/PropertyEdit";
import ObjectPicker from "@v2/components/controls/ObjectPicker";
import { FormControl } from "@v2/components/controls/FormControl/FormControl";
import { FlowstepButtons } from "@v2/components/flowsteps/editors/common/FlowstepButtons/FlowstepButtons";
import GroupPane from "src/components/base/GroupPane";
import SearchableComboBox from "src/components/base/SearchableComboBox";
import { FilterCondition, ProviderType, PullMode } from "shared/types/flowstep";
import { pullModes } from "../../common/states/pull-settings-state";
import { useStyles } from "./PullSalesforceReport.styles";
import { AdvancedSettings } from "@v2/components/flowsteps/editors/common/AdvancedSettings/AdvancedSettings";
import { PullOptions } from "@v2/components/flowsteps/editors/common/PullOptions/PullOptions";
import { FieldList } from "@v2/components/flowsteps/editors/common/FieldList/FieldList";
import { ModalPage } from "src/components/base/Page";
import { FilterManager } from "@v2/components/controls/FilterManager/FilterManager";
import { getOperatorsForType } from "@v2/components/flowsteps/editors/salesforce/report/filters/sf-operators";
import { renderEditorForTypeAndOperator } from "./filters/sf-filter-editors";
import UnsavedChangesDialog from "src/v2/hooks/UnsavedChangesDialog";

interface PullSalesforceReportProps {
  id?: string;
  createNew?: boolean;
  providerType?: ProviderType;
}

export const PullSalesforceReport: React.FC<PullSalesforceReportProps> = observer(({ id, createNew, providerType = ProviderType.SalesforceReport }) => {
  const [state] = useState(() => new PullSalesforceReportState(providerType))
  const [advancedCollapsed, setAdvancedCollapsed] = useState(true);
  const [optionsCollapsed, setOptionsCollapsed] = useState(true);
  const [isFilterManagerOpen, setFilterManagerOpen] = useState(false);
  const styles = useStyles();

  useEffect(() => {
    (async () => {
      await state.loadFlowstep(id, createNew);
    })();
  }, [id, createNew]);

  const handleFilterApply = (conditions: FilterCondition[]) => {
    state.pullSettings.conditions = conditions;
    setFilterManagerOpen(false);
  };

  const title = `${state.isNewFlowstep ? 'New: ' : ''}From ${state.pullSettings.selectedObject?.label || 'Reports'}`;
  let sectionNumber = 1;

  return (<>
    <UnsavedChangesDialog
      onSave={() => state.saveFlowstep()}
      isNotSaved={() => state.isNotSaved}
    />
    <TitledPage title={title}>
      <FlowstepContainer>
        <PropertyEdit
          label="Create new worksheet"
          tooltip={"We'll create a new worksheet for you and pull the data there"}
        >
          <Checkbox
            checked={state.pullSettings.createNewSheet}
            onChange={(_, checked) => state.pullSettings.createNewSheet = !!checked}
          />
        </PropertyEdit>

        <FormControl label={`${sectionNumber++}. Select the report to get data from`}>
          <ObjectPicker
            value={state.pullSettings.selectedObject}
            data={state.reports}
            onChange={(_, newValue) => state.setSelectedReport(newValue)}
            disabled={state.isLoadingReports}
            errorMessage={state.validationErrors.get('object-type-not-selected')}
            placeholder="Select report"
            onSearch={(query) => state.setReportSearchString(query)}
            immediateSearch={true}
          />
        </FormControl>

        <FormControl label={`${sectionNumber++}. Select fields to return`}>
          <FieldList
            searchPlaceholder='Filter headers'
            onChange={(fieldNames) => state.pullSettings.selectedFieldNames = fieldNames}
            selectedFieldNames={state.pullSettings.selectedFieldNames}
            items={state.pullSettings.objectFields.data}
            disabled={!state.pullSettings.objectFields.isReady}
          />
        </FormControl>

        <FormControl label={`${sectionNumber++}. Setup filters (optional)`}>
          <PrimaryButton
            onClick={() => setFilterManagerOpen(true)}
            className={styles.filterManagerButton}
            disabled={!state.pullSettings.objectFields.data?.length}
          >
            Filter manager ({state.pullSettings.conditions.length})
          </PrimaryButton>
        </FormControl>

        <ModalPage
          isOpen={isFilterManagerOpen}
          title="Filter manager"
          onClose={() => setFilterManagerOpen(false)}
          helpLink={undefined}
          component={() => (
            <FilterManager
              objectFields={state.pullSettings.objectFields.data}
              conditions={state.pullSettings.conditions}
              getFieldOperators={(filedType) => getOperatorsForType(filedType)}
              getFieldEditor={renderEditorForTypeAndOperator}
              onApply={handleFilterApply}
            />
          )}
        />

        <FormControl label={`${sectionNumber++}. Select pull mode`}>
          <SearchableComboBox
            options={pullModes}
            selectedKey={state.pullSettings.pullMode}
            onChange={(_, selectedItem) => state.pullSettings.pullMode = selectedItem.key}
            placeholder="Pull mode option"
          />
          <div className={styles.fieldDescription}>
            {pullModes.find(mode => mode.key === state.pullSettings.pullMode)?.description}
          </div>
          {state.pullSettings.pullMode === PullMode.APPEND &&
            <PropertyEdit
              label={'Add Date/Time column'}
              tooltip={'Create a column with the date and time of execution for each run.'}
            >
              <Checkbox
                checked={state.pullSettings.withDateTimeColumn}
                onChange={(_, checked) => state.pullSettings.withDateTimeColumn = !!checked}
                className={styles.pullModeCheckbox}
              />
            </PropertyEdit>
          }
        </FormControl>

        <GroupPane
          title="Options"
          collapsed={optionsCollapsed}
          onCollapsed={(collapsed: boolean) => setOptionsCollapsed(collapsed)}
          tooltip=""
          className={styles.groupPane}
        >
          <PullOptions state={state.pullOptions} />
          <PropertyEdit
            label="Force formula recalculation when done"
            tooltip={'Recalculates all workbook formulas and pivot tables after execution of this flow step'}
          >
            <Checkbox
              checked={state.recalculateFormulas}
              onChange={(_, checked) => state.recalculateFormulas = !!checked}
            />
          </PropertyEdit>
        </GroupPane>

        <GroupPane
          title="Advanced"
          collapsed={advancedCollapsed}
          onCollapsed={(collapsed: boolean) => setAdvancedCollapsed(collapsed)}
          tooltip=""
          className={styles.groupPane}
        >
          <AdvancedSettings
            baseEditorState={state}
            namePlaceholder={state.generatedFlowstepName}
            timestampCellProps={{
              allowNewSheet: true,
              getTimestampSheetId: () => state.getTimestampSheetId(),
            }}
          />
        </GroupPane>

        <FlowstepButtons
          editorState={state}
          disabled={state.isFlowstepButtonsDisabled}
        />
      </FlowstepContainer>
    </TitledPage>
  </>)
});
